import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Video,
  Phone,
  Building2,
  Settings2,
  AlertCircle,
  Loader2,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Globe,
} from "lucide-react";
import { publicBookingApi } from "../services/api";
import KavoaLogo from "../assets/KavoaLogo.png";

// ── Types ────────────────────────────────────────────────────────────

interface ManageData {
  booking: {
    _id: string;
    bookedByName: string;
    bookedByEmail: string;
    startAt: string;
    endAt: string;
    timezone: string;
    status: string;
    answers: { questionLabel: string; answer: string }[];
    cancellationReason: string | null;
    meetingUrl: string | null;
  };
  bookingType: {
    name: string;
    slug: string;
    duration: number;
    location: { type: string; details: string | null } | null;
    timezone: string;
  } | null;
  teamMember: { name: string; email: string } | null;
  firm: { name: string; branding: any } | null;
}

interface SlotDay {
  date: string;
  slots: string[];
}

type PageState = "loading" | "loaded" | "cancelled" | "rescheduled" | "error";

// ── Helpers ──────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getLocationIcon(type?: string) {
  switch (type) {
    case "video":
      return Video;
    case "phone":
      return Phone;
    case "in_person":
      return Building2;
    default:
      return Settings2;
  }
}

function getLocationLabel(loc?: { type: string; details: string | null } | null): string {
  if (!loc) return "TBD";
  if (loc.details) return loc.details;
  switch (loc.type) {
    case "video":
      return "Video call";
    case "phone":
      return "Phone call";
    case "in_person":
      return "In-person";
    default:
      return "TBD";
  }
}

function getMonthDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days: (Date | null)[] = [];

  // Pad start
  for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));

  return days;
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Component ────────────────────────────────────────────────────────

const ManageBookingPage: React.FC = () => {
  const { manageToken } = useParams<{ manageToken: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [data, setData] = useState<ManageData | null>(null);
  const [error, setError] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const fetchedRef = useRef(false);

  // Reschedule state
  const [showReschedule, setShowReschedule] = useState(false);
  const [slots, setSlots] = useState<SlotDay[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    if (!manageToken || fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchData = async () => {
      try {
        const res = await publicBookingApi.getManageData(manageToken);
        const d = res.data;
        setData(d);
        setState(d.booking.status === "cancelled" ? "cancelled" : "loaded");
      } catch (err: any) {
        setError(
          err?.response?.data?.error || "Booking not found or link has expired."
        );
        setState("error");
      }
    };

    fetchData();
  }, [manageToken]);

  // Fetch slots when reschedule mode is shown or month changes
  const fetchSlots = useCallback(async () => {
    if (!manageToken || !showReschedule) return;
    setSlotsLoading(true);
    try {
      const startDate = `${calMonth.year}-${String(calMonth.month + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(calMonth.year, calMonth.month + 1, 0).getDate();
      const endDate = `${calMonth.year}-${String(calMonth.month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

      const res = await publicBookingApi.getSlotsByToken(manageToken, { startDate, endDate });
      setSlots(res.data?.data || []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [manageToken, showReschedule, calMonth]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const handleCancel = async () => {
    if (!manageToken) return;
    setCancelling(true);
    try {
      await publicBookingApi.cancelBooking(manageToken, {
        reason: cancelReason.trim() || undefined,
      });
      setState("cancelled");
      if (data) {
        setData({
          ...data,
          booking: {
            ...data.booking,
            status: "cancelled",
            cancellationReason: cancelReason.trim() || null,
          },
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to cancel booking.");
    } finally {
      setCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  const handleReschedule = async () => {
    if (!manageToken || !selectedSlot) return;
    setRescheduling(true);
    setError("");
    try {
      const res = await publicBookingApi.rescheduleBooking(manageToken, {
        newStartAt: selectedSlot,
      });
      // Update the page with the new booking data
      const newManageToken = res.data?.booking?.manageToken;
      setState("rescheduled");
      if (data && res.data?.booking) {
        setData({
          ...data,
          booking: {
            ...data.booking,
            startAt: res.data.booking.startAt,
            endAt: res.data.booking.endAt,
            status: "confirmed",
          },
        });
      }
      // If we got a new manage token, update the URL without navigating
      if (newManageToken && newManageToken !== manageToken) {
        window.history.replaceState(null, "", `/book/manage/${newManageToken}`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to reschedule booking.");
    } finally {
      setRescheduling(false);
    }
  };

  const accentColor = "#0F5132";

  // Calendar helpers
  const days = getMonthDays(calMonth.year, calMonth.month);
  const monthLabel = new Date(calMonth.year, calMonth.month).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const availableDates = new Set(slots.filter((s) => s.slots.length > 0).map((s) => s.date));

  const selectedDaySlots = selectedDate
    ? slots.find((s) => s.date === selectedDate)?.slots || []
    : [];

  const goNextMonth = () => {
    setCalMonth((prev) => {
      const next = prev.month === 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: prev.month + 1 };
      return next;
    });
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const goPrevMonth = () => {
    const now = new Date();
    setCalMonth((prev) => {
      const next = prev.month === 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: prev.month - 1 };
      // Don't go before current month
      if (next.year < now.getFullYear() || (next.year === now.getFullYear() && next.month < now.getMonth())) {
        return prev;
      }
      return next;
    });
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  // ── Loading ─────────────────────────────────────────────────────────

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center"
        >
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: accentColor }} />
          <p className="text-sm text-slate-500">Loading your booking...</p>
        </motion.div>
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────

  if (state === "error") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center max-w-md"
        >
          <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Booking Not Found
          </h2>
          <p className="text-sm text-slate-500">{error}</p>
        </motion.div>
      </div>
    );
  }

  if (!data) return null;

  const { booking, bookingType, teamMember, firm } = data;
  const LocationIcon = getLocationIcon(bookingType?.location?.type);
  const isCancelled = state === "cancelled" || booking.status === "cancelled";
  const isRescheduled = state === "rescheduled";

  // ── Main ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div
        className="py-4 px-6 text-center"
        style={{ backgroundColor: accentColor }}
      >
        <p className="text-white font-bold text-lg">
          {firm?.name || "Your Firm"}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center p-4 sm:p-8 pt-8 sm:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-200 w-full max-w-lg overflow-hidden"
        >
          {/* Status Header */}
          <div
            className="px-6 py-5 text-center"
            style={{
              backgroundColor: isCancelled
                ? "#fef2f2"
                : isRescheduled
                ? "rgba(15, 81, 50, 0.08)"
                : "rgba(15, 81, 50, 0.05)",
            }}
          >
            {isCancelled ? (
              <>
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <X className="h-6 w-6 text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-red-700">
                  Booking Cancelled
                </h2>
              </>
            ) : isRescheduled ? (
              <>
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: "rgba(15, 81, 50, 0.15)" }}
                >
                  <RefreshCw className="h-6 w-6" style={{ color: accentColor }} />
                </div>
                <h2 className="text-lg font-bold text-slate-800">
                  Booking Rescheduled
                </h2>
                <p className="text-sm text-slate-500 mt-1">Your booking has been moved to the new time below.</p>
              </>
            ) : (
              <>
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: "rgba(15, 81, 50, 0.15)" }}
                >
                  <Check className="h-6 w-6" style={{ color: accentColor }} />
                </div>
                <h2 className="text-lg font-bold text-slate-800">
                  {booking.status === "pending"
                    ? "Booking Pending Approval"
                    : "Booking Confirmed"}
                </h2>
              </>
            )}
          </div>

          {/* Details */}
          <div className="p-6 space-y-5">
            {/* Meeting name */}
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                {bookingType?.name || "Meeting"}
              </h3>
              {teamMember && (
                <p className="text-sm text-slate-500 mt-1">
                  with {teamMember.name}
                </p>
              )}
            </div>

            {/* Info rows */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-4.5 w-4.5 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {formatDateTime(booking.startAt)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-4.5 w-4.5 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {formatTime(booking.startAt)} – {formatTime(booking.endAt)}
                  {bookingType?.duration && (
                    <span className="text-slate-400 ml-1">
                      ({bookingType.duration} min)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="h-4.5 w-4.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-500">
                  {booking.timezone?.replace(/_/g, " ") || "Eastern Time"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <LocationIcon className="h-4.5 w-4.5 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {getLocationLabel(bookingType?.location)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-4.5 w-4.5 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {booking.bookedByName} ({booking.bookedByEmail})
                </span>
              </div>
            </div>

            {/* Video Meeting Link */}
            {booking.meetingUrl && !isCancelled && (
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-center">
                <p className="text-xs font-semibold text-blue-700 mb-2">Video Meeting Link</p>
                <a
                  href={booking.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors"
                >
                  <Video className="h-4 w-4" />
                  Join Meeting
                </a>
                <p className="text-xs text-slate-500 mt-2 break-all">
                  {booking.meetingUrl}
                </p>
              </div>
            )}

            {/* Answers */}
            {booking.answers && booking.answers.length > 0 && (
              <div className="border-t border-slate-100 pt-4 space-y-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Your Responses
                </p>
                {booking.answers.map((a, i) => (
                  <div key={i}>
                    <p className="text-xs text-slate-500">{a.questionLabel}</p>
                    <p className="text-sm text-slate-700">{a.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Cancellation reason */}
            {isCancelled && booking.cancellationReason && (
              <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                <p className="text-xs font-medium text-red-600 mb-1">
                  Cancellation Reason
                </p>
                <p className="text-sm text-red-700">
                  {booking.cancellationReason}
                </p>
              </div>
            )}

            {/* Reschedule Flow */}
            <AnimatePresence mode="wait">
              {showReschedule && !isCancelled && !isRescheduled && (
                <motion.div
                  key="reschedule"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-slate-100 pt-5 space-y-4 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">Pick a new time</p>
                    <button
                      onClick={() => { setShowReschedule(false); setSelectedDate(null); setSelectedSlot(null); }}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      Cancel
                    </button>
                  </div>

                  {/* Mini Calendar */}
                  <div className="border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        onClick={goPrevMonth}
                        className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4 text-slate-600" />
                      </button>
                      <span className="text-sm font-semibold text-slate-700">{monthLabel}</span>
                      <button
                        onClick={goNextMonth}
                        className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4 text-slate-600" />
                      </button>
                    </div>

                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                        <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">
                          {d}
                        </div>
                      ))}
                    </div>

                    {/* Calendar grid */}
                    {slotsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-7 gap-1">
                        {days.map((day, i) => {
                          if (!day) return <div key={`empty-${i}`} />;
                          const dateStr = toDateStr(day);
                          const hasSlots = availableDates.has(dateStr);
                          const isSelected = selectedDate === dateStr;
                          const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));

                          return (
                            <button
                              key={dateStr}
                              onClick={() => { if (hasSlots && !isPast) { setSelectedDate(dateStr); setSelectedSlot(null); } }}
                              disabled={!hasSlots || isPast}
                              className={`aspect-square rounded-lg text-xs font-medium transition-all flex items-center justify-center ${
                                isSelected
                                  ? "text-white shadow-sm"
                                  : hasSlots && !isPast
                                  ? "text-slate-700 hover:bg-slate-100"
                                  : "text-slate-300 cursor-default"
                              }`}
                              style={isSelected ? { backgroundColor: accentColor } : {}}
                            >
                              {day.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Time slots */}
                  {selectedDate && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">
                        Available times for {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                      </p>
                      {selectedDaySlots.length === 0 ? (
                        <p className="text-sm text-slate-400">No available times</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                          {selectedDaySlots.map((slot) => {
                            const isChosen = selectedSlot === slot;
                            return (
                              <button
                                key={slot}
                                onClick={() => setSelectedSlot(slot)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                                  isChosen
                                    ? "text-white border-transparent shadow-sm"
                                    : "text-slate-700 border-slate-200 hover:border-slate-300"
                                }`}
                                style={isChosen ? { backgroundColor: accentColor } : {}}
                              >
                                {new Date(slot).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Confirm reschedule */}
                  {selectedSlot && (
                    <button
                      onClick={handleReschedule}
                      disabled={rescheduling}
                      className="w-full px-4 py-3 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50"
                      style={{ backgroundColor: accentColor }}
                    >
                      {rescheduling
                        ? "Rescheduling..."
                        : `Reschedule to ${new Date(selectedSlot).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            {!isCancelled && !isRescheduled && !showReschedule && (
              <div className="border-t border-slate-100 pt-5">
                {showCancelConfirm ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-red-600">
                      Are you sure you want to cancel this booking?
                    </p>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Reason for cancellation (optional)"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
                      rows={2}
                    />
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Keep Booking
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {cancelling ? "Cancelling..." : "Cancel Booking"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowReschedule(true)}
                      className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reschedule
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="flex-1 px-4 py-3 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Cancel Booking
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && !["loading", "error"].includes(state) && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center space-y-1">
        <p className="text-xs text-slate-400">Powered by</p>
        <a
          href="https://kavoa.io"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex flex-col items-center gap-1 group"
        >
          <img src={KavoaLogo} alt="Kavoa" className="h-10 object-contain" />
          <span className="text-[10px] text-slate-400 group-hover:text-slate-600 transition-colors">
            kavoa.io
          </span>
        </a>
      </div>
    </div>
  );
};

export default ManageBookingPage;
